import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { acknowledgeEmergencyAlert } from '../lib/api';

const AlertContext = createContext();

export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
};

export const AlertProvider = ({ children }) => {
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [isAlertMode, setIsAlertMode] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const audioRef = useRef(null);
  const audioIntervalRef = useRef(null);

  // Check for critical alerts
  useEffect(() => {
    setIsAlertMode(criticalAlerts.length > 0);
  }, [criticalAlerts]);

  // Handle audio alarm
  useEffect(() => {
    if (isAlertMode && audioEnabled) {
      // Play alarm sound on loop
      const playAlarm = () => {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(err => {
            console.log('Audio autoplay blocked:', err);
          });
        }
      };
      
      playAlarm();
      // Repeat every 3 seconds
      audioIntervalRef.current = setInterval(playAlarm, 3000);
    } else {
      // Stop alarm
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
        audioIntervalRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }

    return () => {
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
      }
    };
  }, [isAlertMode, audioEnabled]);

  // Enable audio on user interaction (browser autoplay policy)
  const enableAudio = useCallback(() => {
    setAudioEnabled(true);
  }, []);

  // Add a critical alert
  const addAlert = useCallback((alert) => {
    setCriticalAlerts(prev => {
      // Prevent duplicates
      if (prev.find(a => a.id === alert.id)) {
        return prev;
      }
      return [...prev, { ...alert, timestamp: new Date() }];
    });
  }, []);

  // Add multiple alerts at once
  const setAlerts = useCallback((alerts) => {
    setCriticalAlerts(alerts.map(a => ({ ...a, timestamp: new Date() })));
  }, []);

  // Acknowledge and remove an alert
  const acknowledgeAlert = useCallback(async (alertId, alertType = 'emergency') => {
    try {
      await acknowledgeEmergencyAlert(alertId, alertType);
      setCriticalAlerts(prev => prev.filter(a => a.id !== alertId));
      return true;
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
      return false;
    }
  }, []);

  // Acknowledge all alerts
  const acknowledgeAllAlerts = useCallback(async () => {
    const results = await Promise.all(
      criticalAlerts.map(alert => acknowledgeEmergencyAlert(alert.id, alert.type))
    );
    if (results.every(Boolean)) {
      setCriticalAlerts([]);
    }
  }, [criticalAlerts]);

  // Clear all alerts (local only, no API call)
  const clearAlerts = useCallback(() => {
    setCriticalAlerts([]);
  }, []);

  const value = {
    criticalAlerts,
    isAlertMode,
    audioEnabled,
    addAlert,
    setAlerts,
    acknowledgeAlert,
    acknowledgeAllAlerts,
    clearAlerts,
    enableAudio,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
      {/* Hidden audio element for alarm */}
      <audio
        ref={audioRef}
        src="/alarm.wav"
        preload="auto"
        style={{ display: 'none' }}
      />
    </AlertContext.Provider>
  );
};

export default AlertContext;
